import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import Badge from "@mui/material/Badge";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import Typography from "@mui/material/Typography";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDAvatar from "components/MDAvatar";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";
import profileImage from "assets/images/team-2.jpg";
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
  setSelectedCompany,
} from "context";
import { useNavigate } from "react-router-dom";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    transparentNavbar,
    fixedNavbar,
    openConfigurator,
    darkMode,
    selectedCompanyId,
  } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [openAccountMenu, setOpenAccountMenu] = useState(false);
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(true);
  const [empresas, setEmpresas] = useState([]);
  const navigate = useNavigate();
  const route = useLocation().pathname.split("/").slice(1);

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  useEffect(() => {
    import("config/api").then((api) => {
      import("axios").then((axios) => {
        axios.default.get(api.default.EMPRESAS).then((response) => {
          setEmpresas(response.data || []);
        });
      });
    });
  }, []);

  const handleExitCompany = () => {
    setSelectedCompany(dispatch, null);
    navigate("/dashboard");
  };

  const selectedCompany = empresas.find((e) => String(e.id) === String(selectedCompanyId));
  useEffect(() => {
    // Ejemplo: cada 30 segundos simula una nueva notificación
    // En producción, esto debería ser un WebSocket o polling a tu API
    const interval = setInterval(() => {
      // Aquí puedes agregar lógica para verificar nuevas notificaciones desde tu backend
      // Por ahora solo es una demostración
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleOpenMenu = (event, menuType) => {
    if (menuType === "account") {
      setOpenAccountMenu(event.currentTarget);
    } else {
      setOpenMenu(event.currentTarget);
      // Marcar notificaciones como leídas al abrir el menú
      setHasUnreadNotifications(false);
    }
  };
  const handleCloseMenu = (menuType) => {
    if (menuType === "account") {
      setOpenAccountMenu(false);
    } else {
      setOpenMenu(false);
    }
  };

  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={Boolean(openMenu)}
      onClose={() => handleCloseMenu("notifications")}
      disableScrollLock={true}
      sx={{
        mt: 2,
        "& .MuiPaper-root": {
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
          borderRadius: "12px",
          minWidth: "320px",
        },
      }}
    >
      <MDBox px={2} py={1.5}>
        <MDTypography variant="h6" fontWeight="medium">
          Notificaciones
        </MDTypography>
      </MDBox>
      <Divider sx={{ my: 0 }} />
      <NotificationItem icon={<Icon>email</Icon>} title="Check new messages" />
      <NotificationItem
        icon={<Icon>attach_money</Icon>}
        title="Aumento en las ventas de hoy +15%."
      />
      <NotificationItem
        icon={<Icon>shopping_cart</Icon>}
        title="Transferencia de $5,000 a Innovación Tecnológica S.A."
      />
      <Divider sx={{ my: 0 }} />
      <MenuItem
        component={Link}
        to="/notifications"
        sx={{
          py: 1.5,
          textAlign: "center",
          "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
        }}
      >
        <MDTypography variant="button" color="info" fontWeight="medium">
          Ver todas las notificaciones
        </MDTypography>
      </MenuItem>
    </Menu>
  );

  const renderAccountMenu = () => (
    <Menu
      anchorEl={openAccountMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={Boolean(openAccountMenu)}
      onClose={() => handleCloseMenu("account")}
      disableScrollLock={true}
      sx={{
        mt: 2,
        "& .MuiPaper-root": {
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
          borderRadius: "12px",
          minWidth: "240px",
        },
      }}
    >
      <MDBox px={2} py={2} display="flex" alignItems="center" gap={1.5}>
        <MDAvatar src={profileImage} alt="Profile" size="md" />
        <MDBox>
          <MDTypography variant="button" fontWeight="medium">
            Victor Fernandes
          </MDTypography>
          <MDTypography variant="caption" color="text" display="block">
            Administrador
          </MDTypography>
        </MDBox>
      </MDBox>
      <Divider sx={{ my: 1 }} />
      <MenuItem
        component={Link}
        to="/profile"
        sx={{
          py: 1,
          px: 2,
          "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
        }}
      >
        <ListItemIcon>
          <Icon fontSize="small">person</Icon>
        </ListItemIcon>
        <MDTypography variant="button" fontWeight="regular">
          Mi Perfil
        </MDTypography>
      </MenuItem>
      <MenuItem
        component={Link}
        to="/configuracion"
        sx={{
          py: 1,
          px: 2,
          "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
        }}
      >
        <ListItemIcon>
          <Icon fontSize="small">settings</Icon>
        </ListItemIcon>
        <MDTypography variant="button" fontWeight="regular">
          Configuración
        </MDTypography>
      </MenuItem>
      <Divider sx={{ my: 1 }} />
      <MenuItem
        component={Link}
        to="/authentication/sign-in"
        sx={{
          py: 1,
          px: 2,
          "&:hover": { backgroundColor: "rgba(255, 0, 0, 0.04)" },
        }}
      >
        <ListItemIcon>
          <Icon fontSize="small" color="error">
            logout
          </Icon>
        </ListItemIcon>
        <MDTypography variant="button" fontWeight="regular" color="error">
          Cerrar Sesión
        </MDTypography>
      </MenuItem>
    </Menu>
  );

  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox color={light ? "white" : "inherit"} display="flex" alignItems="center" gap={1.5}>
              {selectedCompany && (
                <MDBox display="flex" alignItems="center" gap={1} mr={2}>
                  <MDBadge
                    badgeContent={selectedCompany.nombre}
                    color="info"
                    variant="gradient"
                    size="lg"
                    container
                  />
                  <Tooltip title="Salir de Empresa" placement="bottom">
                    <IconButton size="small" color="inherit" onClick={handleExitCompany}>
                      <Icon>exit_to_app</Icon>
                    </IconButton>
                  </Tooltip>
                </MDBox>
              )}
              <Tooltip title="Notificaciones" placement="bottom">
                <IconButton
                  name="notificaciones"
                  size="medium"
                  disableRipple
                  color="inherit"
                  sx={{
                    ...navbarIconButton,
                    padding: 1.2,
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                      transition: "all 0.2s ease-in-out",
                    },
                  }}
                  aria-controls="notification-menu"
                  aria-haspopup="true"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenMenu(e, "notifications");
                  }}
                >
                  {hasUnreadNotifications ? (
                    <Badge badgeContent={3} color="error" variant="dot">
                      <Icon sx={{ ...iconsStyle, fontSize: "22px !important" }}>notifications</Icon>
                    </Badge>
                  ) : (
                    <Icon sx={{ ...iconsStyle, fontSize: "22px !important" }}>notifications</Icon>
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Mi Cuenta" placement="bottom">
                <IconButton
                  sx={{
                    ...navbarIconButton,
                    padding: 0.8,
                    border: "2px solid transparent",
                    borderRadius: "50%",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                      borderColor: "primary.main",
                      transform: "scale(1.05)",
                      transition: "all 0.2s ease-in-out",
                    },
                  }}
                  size="medium"
                  disableRipple
                  onClick={(e) => handleOpenMenu(e, "account")}
                >
                  <MDBox position="relative">
                    <MDAvatar
                      src={profileImage}
                      alt="Profile"
                      size="sm"
                      sx={{
                        cursor: "pointer",
                        border: "2px solid",
                        borderColor: "white",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      }}
                    />
                    <MDBox
                      sx={{
                        position: "absolute",
                        bottom: -4,
                        right: -4,
                        backgroundColor: "white.main",
                        borderRadius: "50%",
                        width: "18px",
                        height: "18px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 3px 6px rgba(0,0,0,0.4)",
                        border: "2px solid white",
                      }}
                    >
                      <Icon
                        sx={{
                          fontSize: "18px !important",
                          color: "grey.800",
                          fontWeight: "bold",
                        }}
                      >
                        expand_more
                      </Icon>
                    </MDBox>
                  </MDBox>
                </IconButton>
              </Tooltip>
              <IconButton
                size="medium"
                disableRipple
                color="inherit"
                sx={{ ...navbarMobileMenu, padding: 1.2 }}
                onClick={handleMiniSidenav}
              >
                <Icon sx={{ ...iconsStyle, fontSize: "26px !important" }}>
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              {renderMenu()}
              {renderAccountMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
